import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookMarked,
  CalendarDays,
  Images,
  MapPin,
  Search,
  User,
} from 'lucide-react';
import type { SearchResultItem } from '@familienbaum/shared';
import { useMyPerson, useSearch } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/misc';
import { useIsWide } from '@/lib/use-media-query';
import { cn } from '@/lib/utils';

const ICONS: Record<SearchResultItem['type'], typeof User> = {
  person: User,
  place: MapPin,
  event: CalendarDays,
  media: Images,
  source: BookMarked,
};

const TYPE_LABELS: Record<SearchResultItem['type'], string> = {
  person: 'Person',
  place: 'Ort',
  event: 'Ereignis',
  media: 'Medium',
  source: 'Quelle',
};

/**
 * Was die Suche durchsucht – als Aufzählung unter dem großen Feld.
 *
 * Ein leeres Suchfeld ist eine Frage ohne Angebot: man weiß nicht, ob Namen
 * gemeint sind, Orte, Jahre oder alles. Auf der Übersicht ist die Suche der
 * Einstieg, und dort steht die Antwort deshalb sichtbar darunter statt nur im
 * Platzhalter, den man nach dem ersten Buchstaben nie wieder sieht.
 */
const SCOPES: { type: SearchResultItem['type']; label: string }[] = [
  { type: 'person', label: 'Personen' },
  { type: 'place', label: 'Orte' },
  { type: 'event', label: 'Ereignisse' },
  { type: 'media', label: 'Bilder' },
  { type: 'source', label: 'Quellen' },
];

function routeFor(item: SearchResultItem): string {
  switch (item.type) {
    case 'person':
      return `/personen/${item.id}`;
    case 'place':
      return `/orte?highlight=${item.id}`;
    case 'event':
      return `/ereignisse?highlight=${item.id}`;
    case 'media':
      return `/medien/${item.id}`;
    case 'source':
      return `/quellen?highlight=${item.id}`;
  }
}

/**
 * Feld, Trefferliste und Tastaturbedienung – der Kern, den alle drei Auftritte
 * der Suche teilen.
 *
 * Die Unterschiede sind zwei Schalter. `hero` ist die große Fassung auf der
 * Übersicht: höheres Feld, größere Schrift, die Aufzählung darunter. `flow`
 * setzt die Trefferliste in den Fluss statt als schwebende Ebene – im Blatt
 * auf dem Telefon gibt es kein „darüber", die Liste ist dort der Inhalt.
 */
function SearchBox({
  hero,
  flow,
  autoFocus,
  hotkey,
  onNavigate,
}: {
  hero?: boolean;
  flow?: boolean;
  autoFocus?: boolean;
  /** Cmd/Ctrl+K fokussiert dieses Feld. */
  hotkey?: boolean;
  /** Nach dem Sprung zum Treffer – schließt das Blatt, wenn eines offen ist. */
  onNavigate?: () => void;
}) {
  const wide = useIsWide();
  const navigate = useNavigate();
  // Eigene Kennung je Feld: auf der Übersicht stehen ab `lg` zwei Suchen im
  // Baum – die in der Kopfzeile und die große im Inhalt. Mit einer festen
  // Kennung zeigte `aria-controls` beider Felder auf dieselbe Liste.
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(Boolean(flow));
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 220);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, isFetching } = useSearch(debounced, open);
  const me = useMyPerson();

  /**
   * Die eigene Person zuoberst, wenn sie unter den Treffern ist.
   *
   * Hineingeschmuggelt wird sie nicht: eine Suche, die Unpassendes zeigt, ist
   * kaputt. Aber wenn der Name passt, ist man selbst gemeint – und steht sonst
   * je nach Bestand hinter drei Namensvettern.
   */
  const items = useMemo(() => {
    const found = data?.items ?? [];
    const index = me ? found.findIndex((item) => item.type === 'person' && item.id === me.id) : -1;
    if (index <= 0) return found;
    return [found[index]!, ...found.slice(0, index), ...found.slice(index + 1)];
  }, [data?.items, me?.id]);

  useEffect(() => {
    if (!hotkey) return;
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [hotkey]);

  // Klick außerhalb schließt die Liste. Im Fluss nicht: dort *ist* die Liste
  // der Inhalt des Blattes und verschwindet mit ihm.
  useEffect(() => {
    if (flow) return;
    const handler = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [flow]);

  const go = (item: SearchResultItem) => {
    navigate(routeFor(item));
    setOpen(Boolean(flow));
    setQuery('');
    inputRef.current?.blur();
    onNavigate?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!items.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => (index + 1) % items.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => (index - 1 + items.length) % items.length);
    } else if (event.key === 'Enter') {
      const item = items[active];
      if (item) {
        event.preventDefault();
        go(item);
      }
    }
  };

  const results =
    open && debounced.trim().length >= 2 ? (
      <div
        id={listId}
        role="listbox"
        className={cn(
          'overflow-hidden rounded-2xl bg-surface',
          flow
            ? 'mt-3 border border-line'
            : 'fb-pop absolute top-full right-0 left-0 z-50 mt-2 shadow-float dark:border dark:border-line',
        )}
      >
        {items.length === 0 ? (
          <p className="px-3 py-6 text-center text-[13px] text-ink-3">
            {isFetching ? 'Suche läuft…' : `Nichts zu „${debounced}“ gefunden`}
          </p>
        ) : (
          <ul className={cn('scroll-thin overflow-y-auto p-1', flow ? 'max-h-[50dvh]' : 'max-h-80')}>
            {items.map((item, index) => {
              const Icon = ICONS[item.type];
              return (
                <li key={`${item.type}-${item.id}`}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={index === active}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => go(item)}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-xl px-2.5 text-left',
                      flow ? 'py-2.5' : 'py-2',
                      index === active ? 'bg-sunken' : 'bg-transparent',
                    )}
                  >
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent-strong">
                      <Icon className="size-3.5" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ink">{item.label}</span>
                      {item.sublabel ? (
                        <span className="block truncate text-[12px] text-ink-3">{item.sublabel}</span>
                      ) : null}
                    </span>
                    {me && item.type === 'person' && item.id === me.id ? (
                      <span className="shrink-0 rounded-full bg-brand-soft px-1.5 py-0.5 text-[11px] text-ink-2">
                        Du
                      </span>
                    ) : null}
                    <span className="shrink-0 text-[11px] text-ink-3">{TYPE_LABELS[item.type]}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    ) : null;

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Search
          className={cn(
            'pointer-events-none absolute top-1/2 -translate-y-1/2 text-ink-3',
            hero ? 'left-4 size-5' : 'left-3.5 size-4',
          )}
          aria-hidden
        />
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          type="search"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-label="Archiv durchsuchen"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          // Der lange Platzhalter ist die einzige Stelle, an der steht, *was*
          // die Suche durchsucht: Personen, Orte, Jahre, Ereignisse. „Suchen…"
          // ließ offen, ob sie nur Namen findet. Auf schmalen Feldern wäre er
          // abgeschnitten, deshalb dort die kurze Fassung – und in der großen
          // Fassung steht es zusätzlich als Aufzählung darunter.
          placeholder={
            hero || wide ? 'Nach Person, Ort, Jahr oder Ereignis suchen' : 'Im Archiv suchen…'
          }
          className={cn(
            // 44px auch auf dem Schreibtisch – das ist die eine Stelle, an
            // der der Entwurf über die Standardhöhe hinausgeht: die Suche ist
            // der Einstieg ins Archiv und darf höher stehen als die Knöpfe
            // daneben (40px). Gleich hoch sähen beide wie eine Reihe aus.
            'w-full rounded-full border border-line bg-elevated text-ink shadow-inset',
            hero
              ? 'h-13 pr-4 pl-12 text-base sm:h-14 sm:pl-13 sm:text-[17px]'
              : 'h-11 pr-9 pl-9.5 text-base sm:pr-14 sm:text-sm',
            'placeholder:text-ink-3',
            'transition-[border-color,box-shadow] hover:border-line-strong',
            'focus:border-brand focus:bg-surface focus:outline-none focus:ring-2 focus:ring-brand/25',
            '[&::-webkit-search-cancel-button]:hidden',
          )}
        />
        {isFetching ? (
          <Spinner className={cn('absolute top-1/2 -translate-y-1/2', hero ? 'right-4' : 'right-3')} />
        ) : hero || flow ? null : (
          <kbd className="pointer-events-none absolute top-1/2 right-2.5 hidden -translate-y-1/2 rounded-full border border-line bg-surface px-2 py-0.5 font-mono text-[10.5px] text-ink-3 sm:block">
            ⌘K
          </kbd>
        )}
      </div>

      {/* Die Aufzählung nur, solange nichts getippt ist: sobald Treffer da
          sind, ist die Frage „was findet das hier?" beantwortet, und die Zeile
          wäre nur noch eine Fußnote zwischen Feld und Liste. */}
      {hero && !results ? (
        <ul className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 px-1.5">
          {SCOPES.map((scope) => {
            const Icon = ICONS[scope.type];
            return (
              <li key={scope.type} className="flex items-center gap-1.5 text-[12.5px] text-ink-3">
                <Icon className="size-3.5 shrink-0 text-ink-4" aria-hidden />
                {scope.label}
              </li>
            );
          })}
        </ul>
      ) : null}

      {results}
    </div>
  );
}

/**
 * Die Suche der App, in drei Auftritten.
 *
 *   `field`  Das Feld in der Kopfzeile – ab `lg`, wo Platz dafür ist.
 *   `icon`   Ein Lupenknopf neben dem Konto, der ein Blatt öffnet. Auf dem
 *            Telefon nahm das Feld die halbe Kopfzeile für etwas ein, das man
 *            nicht auf jedem Bildschirm braucht; erreichbar bleibt es überall.
 *   `hero`   Die große Fassung auf der Übersicht, unter der Anrede: dort ist
 *            die Suche kein Werkzeug in der Leiste, sondern der Einstieg.
 */
export function GlobalSearch({ variant = 'field' }: { variant?: 'field' | 'icon' | 'hero' }) {
  const [open, setOpen] = useState(false);

  // Cmd/Ctrl+K öffnet das Blatt. Im Feld liegt derselbe Griff auf dem Fokus –
  // dort weiß nur das Feld, wohin er gehört (siehe `hotkey` in `SearchBox`).
  useEffect(() => {
    if (variant !== 'icon') return;
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [variant]);

  if (variant === 'icon') {
    return (
      <>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Archiv durchsuchen"
          onClick={() => setOpen(true)}
        >
          <Search aria-hidden />
        </Button>

        {/* Radix hängt den Inhalt beim Schließen aus – Feld und Trefferliste
            fangen deshalb beim nächsten Öffnen leer an, ohne eigenes Zutun. */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent title="Im Archiv suchen" size="sm">
            <SearchBox flow autoFocus onNavigate={() => setOpen(false)} />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  if (variant === 'hero') return <SearchBox hero />;

  return (
    <div className="w-full max-w-md">
      <SearchBox hotkey />
    </div>
  );
}
